import log from 'loglevel'
import { select, input, confirm } from '@inquirer/prompts'
import chalk from 'chalk'
import { exec } from '../util/exec';
import { getDeployConfiguration, getEnvironmentConfig } from '../configuration/configuration';


// TODO check if everything is pushed before triggering deploy

export type cliCreateEnvOptions = {
  branch?: string,
  environment?: string,
  deployConfig: string,
}


const isASCII = (str: string) => /^[\x00-\x7F]*$/.test(str);

// Compares two major.minor.patch versions segment by segment
export const isVersionGreater = (next: string, previous: string): boolean => {
  const parse = (version: string): number[] => {
    const match = version.match(/^(\d+)\.(\d+)\.(\d+)/)
    return match ? [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)] : [0, 0, 0]
  }
  const nextParts = parse(next)
  const previousParts = parse(previous)
  for (let i = 0; i < 3; i++) {
    if (nextParts[i] !== previousParts[i]) {
      return nextParts[i] > previousParts[i]
    }
  }
  return false
}

// GIT helpers
const gitGetTags = async () => {
  // first fetch all from remote to make sure we are not missing any tags
  await exec('git fetch --all')
  const result = await exec('git tag')
  const tags = result.replace(/[ \*] /g, '').split('\n');
  return tags
}

const gitCreateTag = async (tag: string, branch: string, remote: string = 'origin', message: string = '') => {
  // TODO tag messages
  await exec(`git tag -f -a ${tag} -m "${message}" ${remote}/${branch}`, { shell: true })
}

const gitPushTag = async (tag: string, remote: string = 'origin') => {
  await exec(`git push -f ${remote} ${tag}`)
}

const gitGetBranches = async () => {
  const result = await exec('git branch')
  const branches = result.replace(/[ \*] /g, '').split('\n');
  const currentBranch = result.split('\n').find(line => line.includes('*')).replace(/[ \*] /g, '')
  return { branches, currentBranch }
}



const cliCreateEnv = async (options: cliCreateEnvOptions) => {
  try {
    const { deployConfig: configFile } = options
    const deployConfig = await getDeployConfiguration(configFile)

    const environments = deployConfig.environments.map(environmentConfig => {
      const { environmentName } = environmentConfig
      return environmentName
    })

    // Extract some info from current tags
    const { branches, currentBranch } = await gitGetBranches()
    const gitTags = await gitGetTags()

    // Environment (CLI option skips the prompt, same as the other answers below)
    const environment = options.environment || await select({
      message: 'Please select environment',
      choices: environments.map(environmentName => ({ value: environmentName })),
      default: 'preview',
    })

    const environmentConfig = getEnvironmentConfig(deployConfig, environment)
    if (!environmentConfig) {
      throw new Error(`Environment "${environment}" is not defined in ${configFile}`)
    }
    const {
      defaultEnvironmentBranch,
      environmentTagCreate,
      environmentTagParseVersion,
      environmentTagIsSemver,
      environmentTagIsVersioned,
      isMultiEnv,
    } = environmentConfig

    // Branch
    const branch = options.branch || await select({
      message: 'Select branch you would like to create environment for',
      choices: branches.map(branchName => ({ value: branchName })),
      default: defaultEnvironmentBranch && branches.includes(defaultEnvironmentBranch) ? defaultEnvironmentBranch : currentBranch,
    })
    if (!isASCII(branch)) {
      throw new Error(`Selected branch ${chalk.yellow(branch)} contains non ASCII characters. Please rename your branch and try again.`)
    }

    const tagBranch = isMultiEnv ? branch : undefined
    const { semverVersion: latestVersion, releaseNumber } = environmentTagParseVersion(environmentTagIsSemver, environmentTagIsVersioned, gitTags, environment, tagBranch)

    // New semver version (only for semver-tagged environments)
    let semverVersion: string | undefined
    if (environmentTagIsSemver) {
      semverVersion = await input({
        message: `Please add new app version - latest version is: ${chalk.green(latestVersion)} (major.minor.patch semver format)`,
        validate: (value: string) => {
          if (!value.match(/^\d+\.\d+\.\d+$/)) {
            return 'Version must be in format [major].[minor].[patch] ex: 3.1.4'
          }
          if (!isVersionGreater(value, latestVersion)) {
            return `Inputted version ${chalk.red(value)} is the same or lower than previous version ${chalk.red(latestVersion)}. Please increment version with every release.`
          }
          return true
        },
      })
    }

    const newTag = environmentTagCreate(environmentTagIsSemver, environmentTagIsVersioned, releaseNumber + 1, semverVersion, environment, tagBranch)

    let confirmationMessage = `Do you really wish to deploy branch ${chalk.green(branch)} to environment ${chalk.green(environment)}. This operation will create tag ${chalk.green(newTag)} from latest commit of branch origin/${chalk.green(branch)} and push it to your remote. Gitlab should handle the rest.`
    if (defaultEnvironmentBranch && branch !== defaultEnvironmentBranch) {
      confirmationMessage += '\n'
      confirmationMessage += chalk.yellow(`WARNING: You are trying to release to ${environment} other branch than default environment branch: ${defaultEnvironmentBranch}. Are you sure you know what you are doing?`)
    }

    const confirmed = await confirm({ message: confirmationMessage })

    // Create and push tag if confirmed
    if (confirmed) {
      await gitCreateTag(newTag, branch)
      await gitPushTag(newTag)
      log.info('Everything seems all right. Check out your gitlab CI/CD page and see if your pipeline is running.')
    } else {
      log.info('Creating tag aborted')
    }

  } catch (error) {
    log.error(error)
    throw Error('Failed to create env')
  }


}

export default cliCreateEnv
